import API_BASE_URL from "../config";
import toast from "react-hot-toast";

export async function login(email, password) {
  const res = await fetch(`${API_BASE_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: 'include',
    body: JSON.stringify({ login: email, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return {
      error: data && data.message ? data.message : `Login failed (${res.status})`,
      status: res.status,
    };
  }
  if (data && data.token) {
    setToken(data.token);
    try { await refreshRolePermissions(); } catch (_) {}
  }
  return data;
}

export function getToken() {
  return localStorage.getItem("token");
}

export function setToken(t) {
  if (t) {
    localStorage.setItem('token', t);
    try { scheduleExpiryNotifications(); } catch (_) {}
  } else {
    localStorage.removeItem('token');
    try { clearExpiryTimers(); } catch (_) {}
  }
}

const ROLE_PERMS_KEY = 'role_permissions';

export function setRolePermissions(perms) {
  if (Array.isArray(perms)) {
    localStorage.setItem(ROLE_PERMS_KEY, JSON.stringify(perms));
  } else {
    localStorage.removeItem(ROLE_PERMS_KEY);
  }
}

export function getRolePermissions() {
  try {
    const raw = localStorage.getItem(ROLE_PERMS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

export async function refreshRolePermissions() {
  const token = getToken();
  if (!token) {
    setRolePermissions([]);
    return [];
  }
  try {
    const res = await fetch(`${API_BASE_URL}/roles/me`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include'
    });
    const data = await res.json().catch(() => ({}));
    const perms = Array.isArray(data.permissions) ? data.permissions : [];
    setRolePermissions(perms);
    return perms;
  } catch (err) {
    return getRolePermissions();
  }
}


function _nativeLogout() {
  // Clear local token and tell server to clear cookie
  try {
    fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
  } catch (_) {}
  localStorage.removeItem("token");
  localStorage.removeItem(ROLE_PERMS_KEY);
}

// --- Token expiry notification helpers ---
let __auth_warn_timeout = null;
let __auth_expire_timeout = null;
let __auth_handled_401 = false;

function clearExpiryTimers() {
  if (__auth_warn_timeout) { clearTimeout(__auth_warn_timeout); __auth_warn_timeout = null; }
  if (__auth_expire_timeout) { clearTimeout(__auth_expire_timeout); __auth_expire_timeout = null; }
}

function scheduleExpiryNotifications() {
  clearExpiryTimers();
  const payload = getTokenPayload();
  if (!payload || !payload.exp) return;
  const expMs = payload.exp * 1000;
  const now = Date.now();
  const msUntilExp = expMs - now;
  if (msUntilExp <= 0) return;

  // Warn 60 seconds before expiry (if possible)
  const warnBefore = 60 * 1000;
  if (msUntilExp > warnBefore) {
    __auth_warn_timeout = setTimeout(() => {
      try { toast.error('Your session will expire in 1 minute. Please save your work.'); } catch (_) {}
    }, msUntilExp - warnBefore);
  }

  // Force logout at expiry (in case server rejects requests)
  __auth_expire_timeout = setTimeout(() => {
    try { _logout_and_clear(); } catch (_) {}
    try { toast.error('Session expired. Please sign in again.'); } catch (_) {}
    try { setTimeout(() => { window.location.href = '/login'; }, 2000); } catch (_) {}
  }, msUntilExp + 500);
}

// Ensure timers are cleared on logout and when token removed
const _origLogout = _nativeLogout;
export function _logout_and_clear() {
  clearExpiryTimers();
  __auth_handled_401 = false;
  _origLogout();
}

// Replace exported logout reference so other modules calling `logout()` still work
export { _logout_and_clear as logout };

// Monkey-patch global fetch to catch 401 responses and force sign-out
if (typeof window !== 'undefined' && window.fetch) {
  const _origFetch = window.fetch.bind(window);
  window.fetch = async function(...args) {
    const resp = await _origFetch(...args);
    const req = args && args.length ? args[0] : null;
    const url = (typeof req === 'string') ? req : (req && req.url ? req.url : '');
    const isAuthEndpoint = typeof url === 'string' && (/\/auth\/login|\/auth\/register|\/auth\/google|\/auth\/facebook/.test(url));
    if (resp && resp.status === 401) {
      // Don't force logout if user isn't signed in or if this is an auth endpoint
      if (!getToken() || isAuthEndpoint) return resp;
      // Avoid multiple alerts
      if (!__auth_handled_401) {
        __auth_handled_401 = true;
        clearExpiryTimers();
        try { _logout_and_clear(); } catch (_) {}
        try { toast.error('Session expired. Please sign in again.'); } catch (_) {}
        try { setTimeout(() => { window.location.href = '/login'; }, 2000); } catch (_) {}
      }
    }
    return resp;
  };
}

// Ensure schedule is set when module loads if token already present
try { if (getToken()) scheduleExpiryNotifications(); } catch (_) {}
// Load role permissions early if token already present
try { if (getToken()) refreshRolePermissions(); } catch (_) {}


// Try to decode JWT and return payload object (or null)
export function getTokenPayload() {
  const t = getToken();
  if (!t) return null;
  try {
    const parts = t.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(atob(payload).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

export function isAdmin() {
  const p = getTokenPayload();
  return p && (p.role === 'admin' || (p.user && p.user.role === 'admin'));
}

export function hasPermission(permission) {
  if (!permission) return false;
  if (isAdmin()) return true;
  const perms = getRolePermissions();
  return perms.includes('admin.all') || perms.includes(permission);
}

export function canAccessAdmin() {
  return isAdmin() || hasPermission('admin.access');
}
